import { useState, useMemo } from "react";
import { useShowMore } from "../../hooks/useShowMore";
import {
    ButtonDiv,
    GalleryGrid,
    GalleryHeader,
    GalleryImage,
    GalleryWrapper,
    ShowMoreButton,
    SubHeader,
} from "./styled";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";
import { useTranslation } from "react-i18next";
import { StaggeredContainer, StaggeredItem } from "../animations/staggeredFadeIn";

const Gallery = ({ images, variant }) => {
    const { t } = useTranslation();
    const { showAll, handleToggle, buttonRef } = useShowMore();
    const [index, setIndex] = useState(-1);

    const galleryTitle = t(`gallery.titles.${variant}`);

    const groups = useMemo(() => {
        const main = images.slice(0, 4);
        const rest = images.slice(4);

        if (variant === "houses") {
            return {
                mode: "houses",
                main,
                first: images.slice(4, 9),
                second: images.slice(9),
            };
        }

        return { mode: "default", main, rest };
    }, [images, variant]);

    const slides = useMemo(() => images.map((img) => ({ src: img.src })), [images]);

    const renderGrid = (imgs, offset = 0) => (
        <GalleryGrid>
            {imgs.map((img, i) => {
                const absoluteIndex = offset + i;

                return (
                    <GalleryImage
                        key={`${variant}-${img.altKey}-${absoluteIndex}`}
                        src={img.src}
                        loading={absoluteIndex < 4 ? "eager" : "lazy"}
                        decoding="async"
                        alt={t(`gallery.galleryAlt.${variant}.${img.altKey}`, {
                            defaultValue: t(`gallery.titles.${variant}`),
                        })}
                        onClick={() => setIndex(absoluteIndex)}
                    />
                );
            })}
        </GalleryGrid>
    );

    const showToggleButton =
        variant === "houses"
            ? (groups.first?.length ?? 0) > 0 || (groups.second?.length ?? 0) > 0
            : (groups.rest?.length ?? 0) > 0;

    return (
        <GalleryWrapper>
            <StaggeredContainer>
                <StaggeredItem>
                    <GalleryHeader>{galleryTitle}</GalleryHeader>
                </StaggeredItem>

                <StaggeredItem>
                    {groups.mode === "houses" ? (
                        <>
                            {renderGrid(groups.main, 0)}
                            {showAll && groups.first.length > 0 && (
                                <>
                                    <SubHeader>{t("gallery.firstHouse")}</SubHeader>
                                    {renderGrid(groups.first, 4)}
                                </>
                            )}
                            {showAll && groups.second.length > 0 && (
                                <>
                                    <SubHeader>{t("gallery.secondHouse")}</SubHeader>
                                    {renderGrid(groups.second, 9)}
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            {renderGrid(groups.main, 0)}
                            {showAll && groups.rest.length > 0 && renderGrid(groups.rest, 4)}
                        </>
                    )}
                </StaggeredItem>

                {showToggleButton && (
                    <StaggeredItem>
                        <ButtonDiv>
                            <ShowMoreButton onClick={handleToggle} ref={buttonRef}>
                                {showAll ? t("gallery.hideMore") : t("gallery.showMore")}
                            </ShowMoreButton>
                        </ButtonDiv>
                    </StaggeredItem>
                )}
            </StaggeredContainer>
            <Lightbox
                open={index >= 0}
                close={() => setIndex(-1)}
                index={index}
                slides={slides}
                plugins={[Zoom]}
                zoom={{ maxZoomPixelRatio: 1.5, zoomInMultiplier: 1 }}
                onBackdropClick={() => setIndex(-1)}
            />
        </GalleryWrapper>
    );
};

export default Gallery;